import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { dynamicLogBaseQuery } from '~/api/common/dynamicBaseQuery';
import { IncomingLogsRequestDto } from '~/api/models/IncomingLogsRequestDto';
import { LogLevelRequestDto } from '~/api/models/LogLevelRequestDto';
import { LogsSearchCriteria } from '~/api/models/logs-search-criteria';
import { PageQuery } from '~/api/models/PageQuery';
import { PagePersistedLogsRequest } from '~/api/models/pagePersistedLogsRequest';
import { CM4LogLevelRequestDto } from '~/api/models/CM4LogLevelRequestDto';
import { BulkResponse } from '~/api/models/BulkResponse';
import { BulkRequest } from '~/api/models/BulkRequest';
import { MultiselectOption } from '@rio-cloud/rio-uikit/Multiselect';

export const LOG_TAG = 'Logs';
export const loggingServiceApi = createApi({
    reducerPath: 'logsApi',
    baseQuery: dynamicLogBaseQuery,
    tagTypes: [LOG_TAG],
    endpoints: (builder) => ({
        requestLogs: builder.mutation<{}, IncomingLogsRequestDto>({
            query: (LogParams: IncomingLogsRequestDto) => ({
                url: `/v1/logs/request-logs`,
                method: 'POST',
                body: LogParams,
            }),
            invalidatesTags: [LOG_TAG],
        }),
        fetchLogs: builder.query<PagePersistedLogsRequest, LogsSearchCriteria & PageQuery>({
            query: (criteria: LogsSearchCriteria & PageQuery) => ({
                url: `/v1/logs/request-logs/search?page=${criteria.pageNumber}&size=${criteria.pageSize}`,
                method: 'POST',
                body: criteria,
            }),
            serializeQueryArgs: ({ queryArgs: { pageNumber, pageSize, ...rest } }) => {
                return JSON.stringify({ ...rest });
            },
            merge: (currentCache, newItems) => {
                if (newItems.number != 0) {
                    const currentCacheAux = [...currentCache.content, ...newItems.content];
                    currentCache.content = [...new Map(currentCacheAux.map(item => [item.id, item])).values()];
                } else {
                    currentCache.content = newItems.content;
                }
            },
            providesTags: [LOG_TAG],
        }),
        fetchSystems: builder.query<MultiselectOption[], string>({
            query: (deviceType: string) => ({
                url: `/v1/logs/device-systems/${deviceType}`,
                method: 'GET',
            }),
            transformResponse: (systems: string[]) => {
                return systems.map(system => ({
                    id: system,
                    label: system?.replace(/_/g, ' '),
                    selected: false,
                }));
            }
        }),
        setLogLevel: builder.mutation<{}, LogLevelRequestDto>({
            query: (LogParams: LogLevelRequestDto) => ({
                url: `/v1/configuration/log-level`,
                method: 'POST',
                body: LogParams,
            }),
        }),
        cm4SetLogLevel: builder.mutation<{}, CM4LogLevelRequestDto>({
            query: (LogParams: CM4LogLevelRequestDto) => ({
                url: `/v1/configuration/log-level-cm4`,
                method: 'POST',
                body: LogParams,
            }),
        }),
        downloadLog: builder.query<string, string>({
            query: (operationId: string) => ({
                url: `/v1/logs/${operationId}/download-url`,
                method: 'GET',
                responseHandler: 'text',

            }),
        }),
        downloadLogZip: builder.query<string, string>({
            query: (contentId: string) => ({
                url: `/v1/logs/${contentId}/download-zip-url`,
                method: 'GET',
                responseHandler: 'text',

            }),
        }),
        downloadLogRequestInBulk: builder.mutation<BulkResponse[], BulkRequest>({
            query: (request: BulkRequest) => ({
                url: `/v1/logs/download-bulk-zips-url`,
                method: 'POST',
                body: request,

            }),

        }),
    })
});

export const {
    useRequestLogsMutation,
    useFetchLogsQuery,
    useFetchSystemsQuery,
    useSetLogLevelMutation,
    useCm4SetLogLevelMutation,
    useLazyDownloadLogQuery,
    useLazyDownloadLogZipQuery,
    useDownloadLogRequestInBulkMutation,
} = loggingServiceApi;
